import React from "react";
import { useList, useCreate, useGo } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import {
  List,
  Tabs,
  Badge,
  Button,
  Typography,
  Space,
  Empty,
  Divider,
  Tag,
  Grid,
  theme,
} from "antd";
import { GlassCard } from "../../shared/components";
import {
  TeamOutlined,
  UserAddOutlined,
  MessageOutlined,
  SettingOutlined,
  BellOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useColorMode } from "../../contexts/color-mode";

const { Title } = Typography;

type Notification = {
  id: number;
  title: string;
  message: string;
  category: "order" | "message" | "system";
  priority: "low" | "normal" | "high";
  isRead: boolean;
  createdAt: string;
  actionUrl?: string;
  metadata?: Record<string, any>;
};

type GroupedNotifications = {
  [key: string]: Notification[];
};

export const NotificationsList: React.FC = () => {
  const { t } = useTranslation();
  const go = useGo();
  const { mutate: create } = useCreate();
  const { mode } = useColorMode();
  const { token } = theme.useToken();
  const breakpoint = Grid.useBreakpoint();
  const isMobile =
    typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;
  const {
    data: notificationsData,
    isLoading,
    refetch,
  } = useList<Notification>({
    resource: "notifications",
    pagination: {
      pageSize: 50,
    },
    filters: [
      {
        field: "channel",
        operator: "in",
        value: ["in_app", "both"],
      },
    ],
  });

  const getNotificationIcon = (category: Notification["category"]) => {
    const iconColor = token.colorSuccess;
    switch (category) {
      case "order":
        return <TeamOutlined style={{ fontSize: 24, color: iconColor }} />;
      case "message":
        return <MessageOutlined style={{ fontSize: 24, color: iconColor }} />;
      case "system":
        return <SettingOutlined style={{ fontSize: 24, color: iconColor }} />;
      default:
        return <BellOutlined style={{ fontSize: 24, color: iconColor }} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 60) {
      return isMobile ? t("notifications.timeAgo.minutesShort", { count: diffInMinutes }) : t("notifications.timeAgo.minutes", { count: diffInMinutes });
    } else if (diffInMinutes < 1440) {
      return isMobile ? t("notifications.timeAgo.hoursShort", { count: Math.floor(diffInMinutes / 60) }) : t("notifications.timeAgo.hours", {
        count: Math.floor(diffInMinutes / 60),
      });
    } else {
      return isMobile ? t("notifications.timeAgo.daysShort", { count: Math.floor(diffInMinutes / 1440) }) : t("notifications.timeAgo.days", {
        count: Math.floor(diffInMinutes / 1440),
      });
    }
  };

  const handleMarkAsRead = (notificationId: number) => {
    create(
      {
        resource: `notifications/${notificationId}/read`,
        values: {},
      },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  const renderNotificationItem = (item: Notification) => (
    <List.Item
      className={`p-4 border-b transition-colors duration-200 ${
        mode === "dark"
          ? "border-gray-700 hover:bg-gray-800"
          : "border-gray-200 hover:bg-gray-50"
      } ${item.actionUrl ? "cursor-pointer" : "cursor-default"}`}
      onClick={() => {
        if (item.actionUrl) {
          go({
            to: item.actionUrl,
          });
        }
      }}
    >
      <List.Item.Meta
        avatar={
          <span
            className={`rounded-full p-2 inline-block ${
              mode === "dark" ? "bg-green-900/30" : "bg-green-50"
            }`}
          >
            {getNotificationIcon(item.category)}
          </span>
        }
        title={
          <span
            className={`font-semibold ${
              mode === "dark" ? "text-gray-100" : "text-gray-800"
            }`}
          >
            {item.title}
          </span>
        }
        description={
          <span className={mode === "dark" ? "text-gray-400" : "text-gray-600"}>
            {item.message}
          </span>
        }
      />
      <div className="flex items-center gap-4">
        <div
          className={`text-sm min-w-[70px] text-right ${
            mode === "dark" ? "text-gray-400" : "text-gray-600"
          }`}
        >
          {formatTimeAgo(item.createdAt)}
        </div>
        {!item.isRead && (
          <Button
            type="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMarkAsRead(item.id);
            }}
            className="text-green-500 hover:text-green-600"
            icon={<CheckOutlined />}
          />
        )}
      </div>
    </List.Item>
  );

  const renderEmpty = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={t("notifications.noNotifications")}
    />
  );

  const groupNotificationsByDate = (
    notifications: Notification[],
  ): GroupedNotifications => {
    const groups: GroupedNotifications = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifications.forEach((notification) => {
      const date = new Date(notification.createdAt);
      date.setHours(0, 0, 0, 0);

      let groupKey: string;

      if (date.getTime() === today.getTime()) {
        groupKey = "today";
      } else if (date.getTime() === yesterday.getTime()) {
        groupKey = "yesterday";
      } else {
        groupKey = date.toISOString().split("T")[0];
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(notification);
    });

    return groups;
  };

  const getGroupTitle = (dateKey: string) => {
    switch (dateKey) {
      case "today":
        return t("notifications.groups.today");
      case "yesterday":
        return t("notifications.groups.yesterday");
      default:
        const date = new Date(dateKey);
        return date.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
    }
  };

  const renderGroupedNotifications = (notifications: Notification[]) => {
    const groupedNotifications = groupNotificationsByDate(notifications);

    return Object.entries(groupedNotifications).map(
      ([dateKey, notifications]) => (
        <div key={dateKey}>
          <Divider orientation="left" className="my-4">
            <span>
              <Tag color="blue" className="text-sm">
                {getGroupTitle(dateKey)}
              </Tag>
            </span>
          </Divider>
          <List
            itemLayout="horizontal"
            dataSource={notifications}
            renderItem={renderNotificationItem}
          />
        </div>
      ),
    );
  };

  return (
    <div className={isMobile ? "px-0 mx-auto" : "p-6 max-w-6xl mx-auto"}>
      <GlassCard className={`w-full shadow-lg`}>
        <Space direction="vertical" size="large" className="w-full">
          {/* The inbox and the notification *preferences* share a word but are
              different objects — link them explicitly rather than leave users
              hunting for where the toggles live. */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <Title
              level={4}
              className={`!mb-0 ${mode === "dark" ? "text-gray-100" : "text-gray-800"}`}
            >
              {t("notifications.notifications")}
            </Title>
            <Link to="/settings?tab=notifications">
              <Button type="link" icon={<SettingOutlined />}>
                {t("notifications.managePreferences", "Gérer mes préférences")}
              </Button>
            </Link>
          </div>
          <Tabs
            defaultActiveKey="all"
            items={[
              {
                key: "all",
                label: (
                  <span className="flex items-center gap-2">
                    {t("notifications.all")}
                    <Badge
                      count={
                        notificationsData?.data?.filter((n) => !n.isRead)
                          .length || 0
                      }
                      className={`${
                        mode === "dark"
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-50 text-green-600"
                      }`}
                    />
                  </span>
                ),
                children: (
                  <div>
                    {notificationsData?.data?.length
                      ? renderGroupedNotifications(notificationsData.data)
                      : renderEmpty()}
                  </div>
                ),
              },
              {
                key: "order",
                label: (
                  <span className="flex items-center gap-2">
                    {t("notifications.tabs.order")}
                    <Badge
                      count={
                        notificationsData?.data?.filter(
                          (n) => n.category === "order" && !n.isRead,
                        ).length || 0
                      }
                      className={`${
                        mode === "dark"
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-50 text-green-600"
                      }`}
                    />
                  </span>
                ),
                children: (
                  <div>
                    {notificationsData?.data?.filter(
                      (n) => n.category === "order",
                    ).length
                      ? renderGroupedNotifications(
                          notificationsData.data.filter(
                            (n) => n.category === "order",
                          ),
                        )
                      : renderEmpty()}
                  </div>
                ),
              },
              {
                key: "message",
                label: (
                  <span className="flex items-center gap-2">
                    {t("notifications.tabs.message")}
                    <Badge
                      count={
                        notificationsData?.data?.filter(
                          (n) => n.category === "message" && !n.isRead,
                        ).length || 0
                      }
                      className={`${
                        mode === "dark"
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-50 text-green-600"
                      }`}
                    />
                  </span>
                ),
                children: (
                  <div>
                    {notificationsData?.data?.filter(
                      (n) => n.category === "message",
                    ).length
                      ? renderGroupedNotifications(
                          notificationsData.data.filter(
                            (n) => n.category === "message",
                          ),
                        )
                      : renderEmpty()}
                  </div>
                ),
              },
              {
                key: "system",
                label: (
                  <span className="flex items-center gap-2">
                    {t("notifications.tabs.system")}
                    <Badge
                      count={
                        notificationsData?.data?.filter(
                          (n) => n.category === "system" && !n.isRead,
                        ).length || 0
                      }
                      className={`${
                        mode === "dark"
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-50 text-green-600"
                      }`}
                    />
                  </span>
                ),
                children: (
                  <div>
                    {notificationsData?.data?.filter(
                      (n) => n.category === "system",
                    ).length
                      ? renderGroupedNotifications(
                          notificationsData.data.filter(
                            (n) => n.category === "system",
                          ),
                        )
                      : renderEmpty()}
                  </div>
                ),
              },
            ]}
          />
        </Space>
      </GlassCard>
    </div>
  );
};
